import {onFind} from "@elements/init-modules-in-scope";
import BlazeSlider from 'blaze-slider';
import {
    addClass,
    findIn,
    on,
    setAttribute,
    removeAttribute,
    toggleClass,
} from "@elements/dom-utils";

// Run a callback once DOM is fully parsed
function runWhenDomReady(cb) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', cb, { once: true });
    } else {
        cb();
    }
}

export function init() {
    onFind('.js-info-notification-slider', function (baseElement) {
        // Controls lookup near this slider (static elements, so we can bind now)
        const controls =
            findIn('.js-info-notification-slider__controls', baseElement);

        const btnPlay = controls && findIn('.js-info-notification-slider__controls-play', controls);
        const btnPause = controls && findIn('.js-info-notification-slider__controls-pause', controls);

        // Convenience references for user interaction targets
        const btnPrev = findIn('.blaze-prev', baseElement);
        const btnNext = findIn('.blaze-next', baseElement);
        const pagination = findIn('.blaze-pagination', baseElement);
        const track = findIn('.blaze-track', baseElement) || findIn('.blaze-track-container', baseElement) || baseElement;

        // Determine autoplay interval from data attribute with a 4000ms default
        const attr = baseElement.getAttribute('data-info-notification-slider-autoplay-time');
        const parsed = attr != null ? parseInt(attr, 10) : NaN;
        const autoplayMs = Number.isNaN(parsed) ? 4000 : parsed;

        const makeConfig = () => ({
            all: {
                loop: true,
                slidesToShow: 1,
                slidesToScroll: 1,
                draggable: false,
                enableAutoplay: true, // will only be instantiated after DOMContentLoaded
                autoplayInterval: autoplayMs,
                stopAutoplayOnInteraction: true,
            },
        });

        // Button visibility helpers (sync hidden + aria-hidden)
        const showBtn = (el) => {
            if (!el) return;
            removeAttribute('hidden', el);
            setAttribute('aria-hidden', 'false', el);
        };
        const hideBtn = (el) => {
            if (!el) return;
            setAttribute('hidden', 'true', el);
            setAttribute('aria-hidden', 'true', el);
        };

        const setPlaying = (isPlaying) => {
            if (!controls) return;
            toggleClass('is-playing',controls);
            toggleClass('is-paused', controls);
            if (btnPlay) setAttribute('aria-pressed', String(isPlaying), btnPlay);
            if (btnPause) setAttribute('aria-pressed', String(!isPlaying), btnPause);

            if (btnPlay && btnPause) {
                if (isPlaying) {
                    showBtn(btnPause);
                    hideBtn(btnPlay);
                } else {
                    showBtn(btnPlay);
                    hideBtn(btnPause);
                }
            }
        };

        let slider = null;

        // Unified pause logic
        const doPause = () => {
            if (slider && typeof slider.stopAutoplay === 'function') {
                slider.stopAutoplay();
            }
            setPlaying(false);
        };

        // Play: destroy + re-init (autoplay on) — called only after DOM is ready
        const doPlay = () => {
            if (slider && typeof slider.destroy === 'function') {
                try { slider.destroy(); } catch (e) { /* noop */ }
            }
            slider = new BlazeSlider(baseElement, makeConfig());
            setPlaying(true);
        };

        // Wire controls
        if (btnPause) {
            on('click', doPause, btnPause);
            on('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 13 || e.keyCode === 32) {
                    e.preventDefault();
                    btnPause.click();
                }
            }, btnPause);
        }

        if (btnPlay) {
            on('click', () => {
                // Ensure DOM is ready before creating the autoplaying slider
                runWhenDomReady(doPlay);
            }, btnPlay);
            on('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 13 || e.keyCode === 32) {
                    e.preventDefault();
                    btnPlay.click();
                }
            }, btnPlay);
        }

        // Mirror Blaze stopAutoplayOnInteraction in UI
        if (btnPrev) on('click', doPause, btnPrev);
        if (btnNext) on('click', doPause, btnNext);
        if (pagination) on('click', doPause, pagination);
        if (track) ['pointerdown', 'mousedown', 'touchstart', 'keydown'].forEach((evt) => on(evt, doPause, track));

        // INITIALIZATION: wait until DOM is fully loaded, then create the autoplaying slider
        runWhenDomReady(() => {
            slider = new BlazeSlider(baseElement, makeConfig());
            // Initial UI: autoplay running → Pause visible, Play hidden
            setPlaying(true);
            if (btnPlay) hideBtn(btnPlay);
            if (btnPause) showBtn(btnPause);
            addClass('initialized', baseElement);
        });
    });
}